import { useState } from "react";
import { Pill, Plus, X, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "Metformin",
  "Lisinopril",
  "Atorvastatin",
  "Levothyroxine",
  "Omeprazole",
  "Sertraline",
  "Ibuprofen",
  "Warfarin",
];

export default function MedicationInput({
  meds,
  setMeds,
  notes,
  setNotes,
  onAnalyze,
  loading,
}) {
  const [draft, setDraft] = useState("");

  const addMed = (value) => {
    const v = (value ?? draft).trim();
    if (!v) return;
    if (meds.some((m) => m.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    setMeds([...meds, v]);
    setDraft("");
  };

  const removeMed = (idx) => {
    setMeds(meds.filter((_, i) => i !== idx));
  };

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addMed();
    } else if (e.key === "Backspace" && !draft && meds.length > 0) {
      removeMed(meds.length - 1);
    }
  };

  return (
    <div
      className="rounded-3xl border p-5 sm:p-8 lg:p-12"
      style={{
        background: "white",
        borderColor: "var(--border-default)",
        boxShadow: "0 1px 0 rgba(28,28,25,0.02)",
      }}
      data-testid="medication-input-card"
    >
      <div className="flex items-center gap-3 mb-6">
        <span
          className="inline-flex h-10 w-10 items-center justify-center rounded-full"
          style={{ background: "var(--brand-light)" }}
        >
          <Pill className="h-5 w-5" style={{ color: "var(--brand)" }} strokeWidth={1.5} />
        </span>
        <div>
          <p className="label-small">Step 01</p>
          <h2
            className="font-serif-display text-2xl sm:text-3xl tracking-tight"
            style={{ color: "var(--text-primary)" }}
          >
            What are you taking?
          </h2>
        </div>
      </div>

      <div
        className="flex items-center gap-2 rounded-2xl border-2 px-3 sm:px-5 py-2 sm:py-3"
        style={{
          borderColor: "var(--border-default)",
          background: "var(--bg-main)",
        }}
      >
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
          {meds.map((m, i) => (
            <span
              key={`${m}-${i}`}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm"
              style={{
                background: "var(--brand-light)",
                color: "var(--brand)",
                borderColor: "rgba(163,177,155,0.4)",
              }}
              data-testid={`med-chip-${i}`}
            >
              {m}
              <button
                onClick={() => removeMed(i)}
                className="opacity-60 hover:opacity-100 transition-opacity min-h-[20px] min-w-[20px] flex items-center justify-center"
                aria-label={`Remove ${m}`}
                data-testid={`remove-med-${i}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            placeholder={meds.length === 0 ? "e.g. Metformin, Lisinopril" : "Add another…"}
            className="flex-1 min-w-[140px] bg-transparent outline-none py-2 text-base"
            style={{ color: "var(--text-primary)" }}
            data-testid="med-input"
          />
        </div>
        <button
          onClick={() => addMed()}
          aria-label="Add medication"
          className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full h-11 min-w-[44px] px-3 sm:px-4 text-sm"
          style={{
            background: "var(--bg-secondary)",
            color: "var(--text-primary)",
          }}
          data-testid="add-med-btn"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Add</span>
        </button>
      </div>

      <p className="mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
        Tap <strong>Add</strong> or press <kbd className="px-1.5 py-0.5 rounded border" style={{ borderColor: "var(--border-default)" }}>Enter</kbd> after each one
      </p>

      {/* Suggestions */}
      <div className="mt-5">
        <p className="label-small mb-2">Try one</p>
        <div className="flex flex-wrap gap-2">
          {SUGGESTIONS.map((s, i) => (
            <button
              key={s}
              onClick={() => addMed(s)}
              className="rounded-full border px-4 py-2.5 text-sm min-h-[44px] transition-all duration-200 hover:-translate-y-0.5"
              style={{
                borderColor: "var(--border-default)",
                background: "transparent",
                color: "var(--text-secondary)",
              }}
              data-testid={`suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="mt-8">
        <label className="label-small block mb-2" htmlFor="notes">
          Optional · context (allergies, conditions)
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="e.g. I have hypertension and prediabetes"
          className="w-full rounded-2xl border-2 px-5 py-3 outline-none text-base resize-none"
          style={{
            borderColor: "var(--border-default)",
            background: "var(--bg-main)",
            color: "var(--text-primary)",
          }}
          data-testid="notes-input"
        />
      </div>

      <div className="mt-8">
        <button
          onClick={onAnalyze}
          disabled={loading || meds.length === 0}
          className="w-full sm:w-auto sm:ml-auto flex items-center justify-center gap-2 rounded-full px-8 py-4 min-h-[52px] text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5 sm:inline-flex"
          style={{ background: "var(--brand)" }}
          data-testid="analyze-btn"
        >
          <Sparkles className="h-4 w-4" strokeWidth={1.75} />
          {loading ? "Analyzing…" : "Reveal insights"}
        </button>
      </div>
    </div>
  );
}
